import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, FileText, Loader2, Link2Off, Clock } from "lucide-react";
import { API_BASE } from "../lib/api";
import { Logo } from "../components/Logo";
import { Markdown } from "../components/Markdown";

export default function SharePage() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    fetch(`${API_BASE}/public/share/${code}`).then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail || "Tautan tidak valid"); setData(await r.json()); }).catch((e) => setError(e.message));
  }, [code]);
  const file = `${API_BASE}/public/share/${code}/file`;
  const btn = "flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-bold";
  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="share-page">
      <header className="flex items-center justify-between border-b border-[#E7ECF3] bg-white px-6 py-4"><Logo size={32} /><a href="/" className="text-xs font-semibold text-[#2F6BFF]">Coba Oryntix</a></header>
      <main className="mx-auto max-w-3xl p-5 sm:p-8">
        {error && <div className="aivora-card p-10 text-center fade-up" data-testid="share-error"><Link2Off size={36} className="mx-auto text-[#EF4444]" /><h1 className="mt-4 text-xl font-bold text-slate-900">Tautan tidak tersedia</h1><p className="mt-1 text-sm text-slate-500">{error}</p></div>}
        {!data && !error && <p className="flex items-center justify-center gap-2 py-20 text-sm text-slate-400"><Loader2 size={16} className="animate-spin" /> Memuat…</p>}
        {data && (
          <div className="fade-up">
            <p className="text-xs text-slate-400">Dibagikan oleh <b className="text-slate-600">{data.shared_by}</b> · <Clock size={11} className="inline" /> berlaku sampai {new Date(data.expires_at).toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
            <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-3xl" data-testid="share-title">{data.title || data.name}</h1>
            <div className="mt-4 flex flex-wrap gap-2">
              {data.kind === "document" ? (<>
                <a href={`${API_BASE}/public/share/${code}/export/docx`} className={`${btn} bg-[#0B132B] text-white`} data-testid="share-dl-docx"><Download size={15} /> Word</a>
                <a href={`${API_BASE}/public/share/${code}/export/pdf`} className={`${btn} bg-slate-200 text-slate-800`} data-testid="share-dl-pdf"><Download size={15} /> PDF</a>
                {data.has_tables && <a href={`${API_BASE}/public/share/${code}/export/xlsx`} className={`${btn} bg-slate-200 text-slate-800`}><Download size={15} /> Excel</a>}
              </>) : data.kind === "plan" ? null : <a href={`${file}?download=1`} className={`${btn} bg-[#0B132B] text-white`} data-testid="share-dl-file"><Download size={15} /> Unduh {data.name}</a>}
            </div>
            <div className="mt-6 aivora-card overflow-hidden p-5" data-testid="share-body">
              {data.kind === "document" && <Markdown content={data.content || "_Dokumen kosong._"} />}
              {data.kind === "image" && <img src={file} alt={data.name} className="mx-auto max-h-[70vh] rounded-xl" />}
              {data.kind === "video" && <video src={file} controls className="w-full rounded-xl" />}
              {data.kind === "file" && <p className="flex items-center gap-2 text-sm text-slate-600"><FileText size={16} /> {data.name}</p>}
              {data.kind === "plan" && <SharedPlan data={data} api={API_BASE.replace(/\/api$/, "")} />}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// kind "plan": a Content Plan's finished parts (comic panels, clips, slides) in story order + the deck files when it is a presentation.
function SharedPlan({ data, api }) {
  const [open, setOpen] = useState(null);
  const KIND = { image: "Komik / seri gambar", video: "Paket klip video", presentation: "Presentasi", document: "Dokumen" };
  const src = (u) => `${api}${u}`;
  const square = data.plan_kind === "image";
  return (
    <div data-testid="share-plan">
      <p className="text-xs text-slate-500">{KIND[data.plan_kind] || data.plan_kind} · {data.parts.length} bagian</p>
      {data.premise && <p className="mt-2 text-sm leading-relaxed text-slate-600" data-no-i18n>{data.premise}</p>}
      {data.files?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="share-plan-files">
          {data.files.map((f, i) => <a key={i} href={src(f.url)} download className="flex items-center gap-1.5 rounded-lg bg-[#0B132B] px-3 py-1.5 text-xs font-bold text-white" data-testid={`share-plan-file-${f.format}`}><FileText size={13} /> {f.format.toUpperCase()} <Download size={12} className="opacity-60" /></a>)}
        </div>
      )}
      <div className={`mt-4 grid gap-3 ${square ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2"}`}>
        {data.parts.map((p) => (
          <figure key={p.n} className="overflow-hidden rounded-xl border border-[#E7ECF3] bg-white" data-testid={`share-part-${p.n}`}>
            {p.type === "video"
              ? <video src={src(p.url)} controls preload="metadata" className="aspect-video w-full bg-black object-contain" />
              : <button type="button" onClick={() => setOpen(p)} className="block w-full"><img src={src(p.url)} alt={p.title || `Bagian ${p.n}`} className={`w-full object-cover ${square ? "aspect-square" : "aspect-video"}`} /></button>}
            <figcaption className="px-3 py-2 text-xs text-slate-600"><span className="font-bold text-slate-900">#{p.n}</span> <span data-no-i18n>{p.title}</span></figcaption>
          </figure>
        ))}
      </div>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4" onClick={() => setOpen(null)} data-testid="share-lightbox">
          <img src={src(open.url)} alt={open.title || ""} className="max-h-[90vh] max-w-full rounded-xl object-contain" />
        </div>
      )}
    </div>
  );
}
